import * as Sentry from "@sentry/nextjs";
import firebase from "firebase/app";
import { Trans, useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";
import { OpenSeaAsset } from "opensea-js/lib/types";
import React, { FC, useEffect, useState } from "react";
import { useCallback } from "react";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { StackedAvatarList } from "@/features/common/components/avatar/StackedAvatarList";
import { TouchableView } from "@/features/common/components/tailwind/TouchableView";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import NFTActionButton from "@/features/nft/components/NFTActionButton";
import { useNftContext } from "@/features/nft/hooks/useNft";
import TextPlaceholder from "@/features/placeholder/TextPlaceholder";
import { NFTDisplayName } from "@/features/post/components/NFTDisplayName";
import {
  CardDate,
  Title,
} from "@/features/post/components/postCard/components";
import { ImageCardLikeButton } from "@/features/post/components/postCard/components/ImageCardLikeButton";
import { ImageCardRemoveLikeButton } from "@/features/post/components/postCard/components/ImageCardRemoveLikeButton";
import { ImageCardProps } from "@/features/post/components/postCard/type";
import {
  useMasonryOpenSeaAssets,
  OpenSeaAssetsStatusEnum,
} from "@/features/post/components/utils/InfiniteMasonryReducer";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { usePostInfoWithLoggedInUserQuery } from "@/generated/generated-hooks";
import convertBigNumberToEther from "@/helpers/convertBigNumberToEther";
import { getPublicConfig } from "@/helpers/getPublicConfig";

const NFTImageCard: FC<ImageCardProps> = (props) => {
  // For i18n translation
  const { t } = useTranslation(["common", "post"]);
  const isBid = false;

  // For error display
  const { openSnackbar } = useSnackbar();

  // 1. get the post object
  const { post } = props;
  // 2. check if the is isRender
  // if isRender, not do the ../../../nft/components/NFTActionButton
  const { isRender = true } = props;

  // 3. check if the component serves as preview when createPost,
  // if yes, return a preview image
  const router = useRouter();
  const { route } = router;
  const isPreview = route === `${RouteName.NewPost}/[slug]`;

  // 4. check if the component is in public landing page
  // if yes: no need fetch subscription plan
  const { user, ready } = useUserContext();
  const isLanding = !user && route === `${RouteName.Landing}`;

  // 5. check if the user owns the post,
  // if yes: no need fetch subscription plan
  const isPostWriter = user?._id === post.writer?._id;

  // 6. fetch the remaining post data: images etc
  const { data: postInfo } = usePostInfoWithLoggedInUserQuery({
    variables: { slug: post.slug },
    context: { batch: true },
    skip: isPreview || !isRender || isLanding || !ready,
  });

  const onClickToPreview = () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.ImageCardClickImage, {
      postSlug: post.slug,
      postId: post._id,
    });

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, preview: post.slug },
      },
      undefined,
      { shallow: true },
    );
  };

  /**
   * THIS PART IS FOR NFT FUNCTIONS
   */
  // get [openSeaAssetHashmap, dispatch] from OpenSeaAssetsContext
  const [openSeaAssetHashmap, dispatch] = useMasonryOpenSeaAssets();
  const [loading, setLoading] = useState<boolean>(false);
  // get the contractAddress
  const {
    service: {
      openSea: { contractAddress },
    },
  } = getPublicConfig();
  // // get current users wallet info & nft api
  const { seaport } = useNftContext();
  // object have the key while the recorded fetch status is success
  const isCached =
    Object.keys(openSeaAssetHashmap).includes(post.tokenId) &&
    openSeaAssetHashmap[post.tokenId].status ===
      OpenSeaAssetsStatusEnum.success;

  const fetchOpenSeaAsset = useCallback(
    async (tokenId: string) => {
      dispatch({ type: "FETCH_START", payload: tokenId });
      try {
        const data: { assets: OpenSeaAsset[]; estimatedCount: number } =
          await seaport.api.getAssets({
            asset_contract_address: contractAddress,
            token_ids: [tokenId],
          });
        // update the OpenSeaAssetsReducer openSeaAssetHashmap
        const fetchedMintedAsset =
          data.assets.length > 0 ? data.assets[0] : null;
        dispatch({ type: "ADD_ASSET", payload: fetchedMintedAsset });
      } catch (err) {
        // if have error, continue fetching
        Sentry.captureException(err.message, {
          tags: { seaport: `post fetching error.`, postId: post._id },
        });
        // record the current fetch time
        dispatch({ type: "FETCH_FAIL", payload: tokenId });
        // setMintedAssetError(err.message);
      }
      return;
    },
    [contractAddress, dispatch, post, seaport],
  );

  useEffect(() => {
    // check if OpenSeaAssetsReducer cached the OpenSeaAsset with the tokenId in the openSeaAssetHashmap yet
    if (!isCached && seaport) {
      // check if fetched before
      if (openSeaAssetHashmap[post.tokenId]) {
        // check if the current time is greater than last fetch time by random 1-3 seconds
        const currentTime = new Date();
        const secondsFromLastFetch =
          (currentTime.getTime() -
            openSeaAssetHashmap[post.tokenId].lastFetchedAt.getTime()) /
          1000;
        const randomSeconds = Math.random() * 4 + 1;
        if (secondsFromLastFetch < randomSeconds) return;
      }

      // if not, fetch data, get response and dispatch action to update the openSeaAssetHashmap
      async function getMintedAsset(tokenId: string) {
        // start fetching
        setLoading(true);
        await fetchOpenSeaAsset(tokenId);
        // end fetching
        setLoading(false);
      }

      getMintedAsset(post.tokenId);
    }
    // else do nothing
  }, [
    contractAddress,
    fetchOpenSeaAsset,
    isCached,
    openSeaAssetHashmap,
    post.tokenId,
    seaport,
  ]);

  // // calculate if current user is the nft owner
  const isNFTOwner = useCallback(
    (asset?: OpenSeaAsset) => {
      return (
        asset &&
        asset.owner.address &&
        user &&
        user.walletAccount === asset.owner.address
      );
    },
    [user],
  );

  return (
    <div
      className={`bg-${
        isPreview ? "white" : "transparent"
      } select-none flex flex-col w-full relative border border-primary rounded-xl overflow-hidden`}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* top-left ribbon */}
      <div className="z-10 absolute top-0 left-0">
        <div className="ribborn-tl" />
      </div>
      {/* ImageArea */}
      <div className="relative">
        <TouchableView onClick={onClickToPreview}>
          <Image
            alt={post.title}
            width={post.previewImage.intrinsicSize.width}
            height={post.previewImage.intrinsicSize.height}
            src={
              postInfo?.post
                ? postInfo.post.images[0].url
                : post.previewImage.url
            }
            layout="responsive"
            onMouseDown={(e) => e.preventDefault()}
            priority
          />
        </TouchableView>

        {/* Stacked Avatar List */}
        <div className="absolute -bottom-6 left-3">
          <div className="flex -space-x-6">
            <StackedAvatarList
              avatars={
                // TODO: handle isBid - future
                isBid
                  ? [
                      { alt: post.title, src: post.previewImage.url },
                      { alt: post.title, src: post.previewImage.url },
                      { alt: post.title, src: post.previewImage.url },
                    ]
                  : [
                      {
                        walletAccount:
                          openSeaAssetHashmap[post.tokenId]?.asset?.owner
                            ?.address,
                      },
                    ]
              }
            />
          </div>
        </div>
      </div>

      {/* Information */}
      <div className="flex flex-col min-w-0 p-4 mt-0 pt-10 space-y-3">
        {/* Creator & Ending Bit Time */}
        <div className="w-full flex justify-between">
          <div className="flex-1 min-w-0">
            {openSeaAssetHashmap[post.tokenId]?.asset ? (
              <span className="truncate">
                <Trans
                  i18nKey="post:imageCard.by"
                  components={{
                    name: (
                      <NFTDisplayName
                        className="text-sm font-light text-gray-900 touchable"
                        walletAccount={
                          openSeaAssetHashmap[post.tokenId]?.asset.owner.address
                        }
                      />
                    ),
                  }}
                />
              </span>
            ) : (
              <TextPlaceholder />
            )}
          </div>
          <div className="ml-5">
            <CardDate date={post.createdAt} />
          </div>
        </div>

        {/* Title */}
        {/* <TouchableView onClick={onClickToPreview}>
          <div className="text-2xl text-black font-bold">{post.title}</div>
        </TouchableView> */}
        <Title
          title={post.title}
          slug={post.slug}
          isPreview={isPreview}
          postId={post._id}
        />

        <div className="flex items-center justify-between w-full">
          {/* if loading or asset not cached --> loading skeleton */}
          {loading ||
          !isCached ||
          !openSeaAssetHashmap[post.tokenId].asset ||
          !openSeaAssetHashmap[post.tokenId] ? (
            <>
              <div className="rounded w-2/4 h-4 bg-gray-200" />
              <div className="rounded-full bg-gray-200 h-9 w-20">
                <TextPlaceholder />
              </div>
            </>
          ) : (
            <>
              <span className="text-sm font-light text-gray-400">
                {/* if listed --> display price, else forSaleSoon */}
                {openSeaAssetHashmap[post.tokenId].asset.sellOrders?.length >
                0 ? (
                  <>
                    <Trans
                      i18nKey="common:nft.sellingPrice"
                      values={{
                        amount: `${convertBigNumberToEther(
                          openSeaAssetHashmap[post.tokenId].asset.sellOrders[0]
                            .basePrice,
                        )} ${
                          openSeaAssetHashmap[post.tokenId].asset.sellOrders[0]
                            .paymentTokenContract.symbol
                        }`,
                      }}
                      components={{
                        price: <span className="text-primary" />,
                      }}
                    />
                  </>
                ) : (
                  <>
                    <div className="text-gray-800  text-base">
                      {t("common:nft.forSaleSoon")}
                    </div>
                  </>
                )}
              </span>
              <NFTActionButton
                isNFTOwner={isNFTOwner(openSeaAssetHashmap[post.tokenId].asset)}
                asset={openSeaAssetHashmap[post.tokenId].asset}
                post={post}
              />
            </>
          )}
        </div>
      </div>
      {/* Utilities and Multi image indicator. */}
      <div className="absolute top-0 right-0 z-30 p-4">
        {postInfo && postInfo.post?.hasCurrentUserLike ? (
          <ImageCardRemoveLikeButton postId={post._id}>
            <div className="bg-gray-800 p-2 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 fill-current text-primary"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </ImageCardRemoveLikeButton>
        ) : (
          <ImageCardLikeButton postId={post._id}>
            <div className="bg-gray-800 p-2 rounded-full bg-opacity-75 hover:bg-opacity-100 cursor-pointer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 fill-current text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </ImageCardLikeButton>
        )}
      </div>
    </div>
  );
};

export default NFTImageCard;
